import {
  CLIENT_BUILD_ID,
  FEEDBACK_ASSIGN_MAX,
  FEEDBACK_GROUP_NONE,
  FEEDBACK_STATUSES,
  FEEDBACK_TYPES,
  type FeedbackType,
  type OperatorFeedbackItem,
  type OperatorFeedbackList,
  type OperatorFeedbackQuery,
  type OperatorFeedbackReport,
} from "@esi/shared";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Inbox, SearchX } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "@/components/EmptyState";
import { NativeSelect } from "@/components/NativeSelect";
import { InlineError, Notice } from "@/components/Notice";
import { Page } from "@/components/Page";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { agentContextParts } from "../feedback/context.ts";
import { ErrorList, GroupValues } from "../feedback/FeedbackDialog.tsx";
import { FeedbackStatus } from "../feedback/FeedbackStatus.tsx";
import { formatDateTime } from "../i18n/format.ts";
import {
  CONSOLE_FEEDBACK_GROUPS_PATH,
  consoleFeedbackGroupPath,
  consoleFeedbackInboxPath,
  consoleFeedbackPath,
  type FeedbackReportOrigin,
  Link,
  navigate,
  useSearch,
} from "../router.tsx";
import { isEditable } from "../shell/sidebar/SearchDialog.tsx";
import { AssistantSuggestion, InboxSuggestion, SuggestUnsorted } from "./FeedbackAssist.tsx";
import {
  AddToGroup,
  FeedbackBreadcrumb,
  FeedbackTabs,
  ReportFingerprints,
  ReportGrouping,
  useFeedbackGroup,
  useGroups,
  useReportGrouping,
} from "./FeedbackGroups.tsx";
import { FeedbackScreenshot } from "./FeedbackScreenshot.tsx";
import {
  type FeedbackFilters as Filters,
  filtersOf,
  NO_FILTERS,
  originOf,
  queryOf,
  searchOf,
  stepOriginOf,
} from "./feedback-filters.ts";
import { useTenants } from "./queries.ts";

const feedbackApi = api.api.operator.feedback;
/** How long Previous/Next reuse the list they step through before reading it again. */
const STEP_LIST_STALE_MS = 30_000;
/** Pages Previous/Next read to find a report not loaded yet (5 × 50 reports). */
const STEP_SEARCH_PAGES = 5;

/** The inbox's list for one filter (never a description, never audited), paged by keyset. */
type InboxList = OperatorFeedbackList & {
  /** An older API refused the Group filter (`400`): listed without it (design D2). */
  groupRefused?: boolean;
};

/**
 * The inbox's pages for `query`: the inbox and the report view's Previous/Next read the same
 * cache entry (feedback-inbox-followups D5). An older API refuses `group` with a `400` during a
 * canary: the list is read again without it and says so (D2), keeping the other filters.
 */
export function useInboxReports(
  query: OperatorFeedbackQuery,
  { enabled = true, staleTime = 0 }: { enabled?: boolean; staleTime?: number } = {},
) {
  return useInfiniteQuery({
    enabled,
    staleTime,
    queryKey: ["console", "feedback", query] as const,
    initialPageParam: undefined as { before: string; beforeId: string } | undefined,
    queryFn: async ({ pageParam }): Promise<InboxList> => {
      try {
        return await ok(await feedbackApi.$get({ query: { ...query, ...pageParam } }));
      } catch (error) {
        if (!(error instanceof ApiError && error.status === 400 && query.group)) {
          throw error;
        }
        const { group: _refused, ...rest } = query;
        const list = await ok(await feedbackApi.$get({ query: { ...rest, ...pageParam } }));
        return { ...list, groupRefused: true };
      }
    },
    getNextPageParam: (last) => {
      const end = last.reports.at(-1);
      return last.more && end ? { before: end.createdAt, beforeId: end.id } : undefined;
    },
    retry: false,
  });
}

/**
 * The Feedback inbox (openspec change `in-app-feedback`, spec "Who reads feedback"): every
 * tenant's reports, newest first, with type, status, build, route, tenant and time; filtered by
 * status, type, tenant and build. No description here: it is read in the report, whose opening
 * is audited. The filters live in the address (feedback-triage-ux D1): a report opened from here
 * and its way back keep them.
 */
export function FeedbackInbox() {
  const { t } = useTranslation();
  const id = useId();
  const tenants = useTenants();
  const filters = filtersOf(useSearch());
  const search = searchOf(filters);
  // A filter change replaces the entry: Back leaves the inbox, not one change.
  const setFilters = (next: Filters) => {
    navigate(consoleFeedbackInboxPath(searchOf(next)), { replace: true });
  };
  // Build is typed: a local draft, written to the address once it is empty or a whole build ID
  // (the only values the API filters on), and on blur. Never one history write per keystroke.
  const [build, setBuild] = useState(filters.build);
  const [shownBuild, setShownBuild] = useState(filters.build);
  if (shownBuild !== filters.build) {
    // The address changed elsewhere (Clear filters, Back): the field follows it.
    setShownBuild(filters.build);
    setBuild(filters.build);
  }
  const commitBuild = (value: string) => {
    if (value.trim() !== filters.build) {
      setFilters({ ...filters, build: value.trim() });
    }
  };
  const tenantKnown =
    !filters.tenantId || (tenants.data ?? []).some((tenant) => tenant.id === filters.tenantId);
  const groups = useGroups();
  const groupKnown =
    filters.group === "all" ||
    filters.group === FEEDBACK_GROUP_NONE ||
    (groups.data ?? []).some((group) => group.id === filters.group);
  const query = queryOf(filters);
  const reports = useInboxReports(query);
  const list = reports.data?.pages.flatMap((p) => p.reports) ?? [];
  const groupRefused = reports.data?.pages.some((p) => p.groupRefused) ?? false;
  // The rows picked for "Add to group…" (D4): at most one request's worth, cleared whenever the
  // filters change, so a selection never covers rows the operator no longer sees.
  const [selected, setSelected] = useState<ReadonlySet<string>>(() => new Set());
  const [selectedFor, setSelectedFor] = useState(search);
  if (selectedFor !== search) {
    setSelectedFor(search);
    setSelected(new Set());
  }
  // A row moved out of the list meanwhile (refetched) is no longer selected.
  const picked = list.filter((r) => selected.has(r.id));
  const toggle = (reportId: string, on: boolean) => {
    const next = new Set(picked.map((r) => r.id));
    if (on && next.size < FEEDBACK_ASSIGN_MAX) {
      next.add(reportId);
    } else if (!on) {
      next.delete(reportId);
    }
    setSelected(next);
  };
  const selectable = list.slice(0, FEEDBACK_ASSIGN_MAX);
  const allPicked = selectable.length > 0 && selectable.every((r) => selected.has(r.id));
  // The column shows once the API says where rows are (an older API sends no `group`).
  const groupColumn = list.some((r) => r.group !== undefined);
  const filtered = search !== "";
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setFilters({ ...filters, [key]: value });
  return (
    <Page width="wide">
      <PageHeader title={t("console.feedback.title")} lead={t("console.feedback.lead")} />
      <FeedbackTabs current="reports" />
      <form
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
        aria-label={t("console.feedback.filters")}
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-status`}>{t("console.feedback.status")}</Label>
          <NativeSelect
            id={`${id}-status`}
            value={filters.status}
            onChange={(e) => set("status", e.currentTarget.value as Filters["status"])}
          >
            <option value="all">{t("console.feedback.any")}</option>
            {FEEDBACK_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`myFeedback.statuses.${s}`)}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-type`}>{t("console.feedback.type")}</Label>
          <NativeSelect
            id={`${id}-type`}
            value={filters.type}
            onChange={(e) => set("type", e.currentTarget.value as Filters["type"])}
          >
            <option value="all">{t("console.feedback.any")}</option>
            {FEEDBACK_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`feedback.types.${type}`)}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-tenant`}>{t("console.feedback.tenant")}</Label>
          <NativeSelect
            id={`${id}-tenant`}
            value={filters.tenantId}
            onChange={(e) => set("tenantId", e.currentTarget.value)}
          >
            <option value="">{t("console.feedback.anyTenant")}</option>
            {/* A tenant ID from the address that the list doesn't hold (yet): say it filters. */}
            {tenantKnown ? null : (
              <option value={filters.tenantId}>{t("console.feedback.unknownTenant")}</option>
            )}
            {(tenants.data ?? []).map((tenant) => (
              <option key={tenant.id} value={tenant.id}>
                {tenant.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-group`}>{t("console.feedback.groupFilter")}</Label>
          <NativeSelect
            id={`${id}-group`}
            value={filters.group}
            aria-describedby={groupRefused ? `${id}-group-error` : undefined}
            aria-invalid={groupRefused || undefined}
            onChange={(e) => set("group", e.currentTarget.value)}
          >
            <option value="all">{t("console.feedback.any")}</option>
            <option value={FEEDBACK_GROUP_NONE}>{t("console.feedback.unsorted")}</option>
            {/* A group ID from the address that the list doesn't hold (yet): say it filters. */}
            {groupKnown ? null : (
              <option value={filters.group}>{t("console.feedback.unknownGroup")}</option>
            )}
            {(groups.data ?? []).map((group) => (
              <option key={group.id} value={group.id}>
                {group.title}
              </option>
            ))}
          </NativeSelect>
          {groupRefused ? (
            <InlineError id={`${id}-group-error`}>
              {t("console.feedback.groupFilterRefused")}
            </InlineError>
          ) : null}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-build`}>{t("console.feedback.build")}</Label>
          <Input
            id={`${id}-build`}
            value={build}
            spellCheck={false}
            maxLength={64}
            placeholder="20261031T101500Z-abc1234"
            onChange={(e) => {
              const value = e.currentTarget.value;
              setBuild(value);
              if (!value.trim() || CLIENT_BUILD_ID.test(value.trim())) {
                commitBuild(value);
              }
            }}
            onBlur={(e) => commitBuild(e.currentTarget.value)}
            className="font-mono text-xs"
          />
        </div>
      </form>
      {/* The triage assistant on the filter's Unsorted reports (feedback-triage-assist D1). */}
      <SuggestUnsorted
        filter={{
          ...(query.type && { type: query.type }),
          ...(query.tenantId && { tenantId: query.tenantId }),
          ...(query.build && { build: query.build }),
        }}
      />
      {reports.isError ? (
        <Notice tone="danger">{reports.error.message}</Notice>
      ) : !reports.data ? (
        <Skeleton className="h-48 w-full" />
      ) : list.length === 0 ? (
        filtered ? (
          <EmptyState
            icon={SearchX}
            title={t("console.feedback.noMatch")}
            action={
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setBuild("");
                  setFilters(NO_FILTERS);
                }}
              >
                {t("console.feedback.clear")}
              </Button>
            }
          >
            {t("console.feedback.noMatchHint")}
          </EmptyState>
        ) : (
          <EmptyState icon={Inbox} title={t("console.feedback.none")}>
            {t("console.feedback.noneHint")}
          </EmptyState>
        )
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col divide-y divide-line rounded-lg border border-line bg-surface-raised">
            <div className="flex min-h-12 flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2">
              <div className="flex items-center gap-2.5">
                <Checkbox
                  id={`${id}-all`}
                  checked={allPicked ? true : picked.length > 0 ? "indeterminate" : false}
                  onCheckedChange={(on) =>
                    setSelected(new Set(on === true ? selectable.map((r) => r.id) : []))
                  }
                />
                <Label htmlFor={`${id}-all`} className="font-normal text-fg-secondary">
                  {picked.length > 0
                    ? t("console.feedback.selected", { count: picked.length })
                    : list.length > selectable.length
                      ? t("console.feedback.selectFirst", { count: selectable.length })
                      : t("console.feedback.selectAll", { count: selectable.length })}
                </Label>
              </div>
              {picked.length > 0 ? (
                <AddToGroup
                  className="flex min-w-0 flex-1 flex-col gap-1.5 sm:items-end"
                  hideLabel
                  reportIds={picked.map((r) => r.id)}
                  currentId={null}
                  type={commonType(picked)}
                  label={t("console.feedback.addSelected")}
                  submitLabel={t("console.feedback.triage.addShort")}
                  onDone={() => setSelected(new Set())}
                />
              ) : null}
            </div>
            <ul className="flex flex-col divide-y divide-line">
              {list.map((report) => (
                <InboxRow
                  key={report.id}
                  report={report}
                  search={search}
                  groupColumn={groupColumn}
                  checked={selected.has(report.id)}
                  disabled={!selected.has(report.id) && picked.length >= FEEDBACK_ASSIGN_MAX}
                  onCheck={(on) => toggle(report.id, on)}
                />
              ))}
            </ul>
          </div>
          {reports.hasNextPage ? (
            <Button
              variant="ghost"
              className="self-start"
              disabled={reports.isFetchingNextPage}
              onClick={() => void reports.fetchNextPage()}
            >
              {t("console.feedback.older")}
            </Button>
          ) : null}
        </div>
      )}
    </Page>
  );
}

/** The new group's type for a selection: theirs when they share one, else a bug. */
function commonType(reports: OperatorFeedbackItem[]): FeedbackType {
  const types = new Set(reports.map((r) => r.type));
  return types.size === 1 ? (reports[0]?.type ?? "bug") : "bug";
}

function InboxRow(props: {
  report: OperatorFeedbackItem;
  search: string;
  groupColumn: boolean;
  checked: boolean;
  disabled: boolean;
  onCheck: (on: boolean) => void;
}) {
  const { t } = useTranslation();
  const { report } = props;
  const title = t("console.feedback.rowTitle", {
    type: t(`feedback.types.${report.type}`),
    tenant: report.tenantName,
  });
  return (
    <li className="flex min-w-0 items-start gap-4 px-4 py-3 sm:items-center">
      <Checkbox
        className="mt-1 sm:mt-0"
        checked={props.checked}
        disabled={props.disabled}
        aria-label={t("console.feedback.selectRow", { title })}
        onCheckedChange={(on) => props.onCheck(on === true)}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <Link
            href={consoleFeedbackPath(report.id, { inbox: props.search })}
            className="font-medium text-fg underline-offset-4 hover:underline"
          >
            {title}
          </Link>
          <span className="truncate font-mono text-xs text-fg-muted">
            {[report.route, report.version, report.build].filter(Boolean).join(" · ") ||
              t("console.feedback.noContext")}
          </span>
        </div>
        {props.groupColumn ? (
          <div className="min-w-0 text-sm sm:w-48 sm:shrink-0">
            {report.group ? (
              <Link
                href={consoleFeedbackGroupPath(report.group.id)}
                className="block truncate text-fg-secondary underline underline-offset-4 decoration-line-strong hover:text-fg hover:decoration-current"
                title={report.group.title}
              >
                {report.group.title}
              </Link>
            ) : (
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-fg-muted">{t("console.feedback.unsorted")}</span>
                {/* The assistant's pending suggestion (feedback-triage-assist), until decided. */}
                <InboxSuggestion report={report} />
              </span>
            )}
          </div>
        ) : null}
        <div className="flex items-center gap-3">
          {/* Sent from a conversation (feedback-from-conversation): where it came from. */}
          {report.origin && report.origin !== "menu" ? (
            <Badge variant="outline">{t(`console.feedback.origin.${report.origin}`)}</Badge>
          ) : null}
          <FeedbackStatus report={report} />
          <time dateTime={report.createdAt} className="tabular text-xs text-fg-muted">
            {formatDateTime(report.createdAt)}
          </time>
        </div>
      </div>
    </li>
  );
}

/**
 * One report as the operator opens it (each opening is in the instance audit log): the
 * description as plain text, never Markdown or HTML, and the context the reporter chose to send.
 */
export function FeedbackReportPage({ reportId }: { reportId: string }) {
  const { t } = useTranslation();
  const search = useSearch();
  const origin = originOf(search);
  const stepFrom = stepOriginOf(search);
  const report = useQuery({
    queryKey: ["console", "feedback", "report", reportId] as const,
    queryFn: async (): Promise<OperatorFeedbackReport> =>
      ok(await feedbackApi[":reportId"].$get({ param: { reportId } })),
    retry: false,
    // Each opening is one audited read: fetched on every mount and never kept once the view
    // closes, and no refetch on focus (that would be a read nobody asked for).
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    gcTime: 0,
  });
  // For the header and the Group block alike (one query, not audited), once the report opened.
  const grouping = useReportGrouping(reportId, report.isSuccess);
  const group = grouping.data?.group ?? null;
  // Back to where the report was opened from (feedback-triage-ux D2): the group, named when the
  // report is still in it, or the inbox with its filters.
  const back =
    "group" in origin ? (
      <FeedbackBreadcrumb
        steps={[
          { label: t("console.feedback.tabGroups"), href: CONSOLE_FEEDBACK_GROUPS_PATH },
          {
            label: group?.id === origin.group ? group.title : t("console.feedback.triage.group"),
            href: consoleFeedbackGroupPath(origin.group),
          },
        ]}
      />
    ) : (
      <FeedbackBreadcrumb
        steps={[
          {
            label: t("console.feedback.tabReports"),
            href: consoleFeedbackInboxPath(origin.inbox),
          },
        ]}
      />
    );
  if (report.isError) {
    return (
      <Page>
        {back}
        <Notice tone="danger">{report.error.message}</Notice>
      </Page>
    );
  }
  if (!report.data) {
    return (
      <Page>
        {back}
        <Skeleton className="h-64 w-full" />
      </Page>
    );
  }
  const data = report.data;
  const context = data.context;
  return (
    <Page width="narrow" className="gap-6">
      <PageHeader
        before={back}
        actions={stepFrom ? <ReportSteps reportId={data.id} from={stepFrom} /> : null}
        title={t("console.feedback.rowTitle", {
          type: t(`feedback.types.${data.type}`),
          tenant: data.tenantName,
        })}
        lead={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <time dateTime={data.createdAt}>{formatDateTime(data.createdAt)}</time>
            <FeedbackStatus report={data} />
            {data.origin && data.origin !== "menu" ? (
              <span>{t(`console.feedback.origin.${data.origin}`)}</span>
            ) : null}
            {grouping.data ? (
              group ? (
                <span className="min-w-0 break-words">
                  {t("console.feedback.triage.inGroup")}{" "}
                  <Link
                    href={consoleFeedbackGroupPath(group.id)}
                    className="font-medium text-fg underline underline-offset-4 decoration-line-strong hover:decoration-current"
                  >
                    {group.title}
                  </Link>
                </span>
              ) : (
                <span>{t("console.feedback.triage.notGrouped")}</span>
              )
            ) : null}
          </span>
        }
      />
      <section aria-labelledby="feedback-description" className="flex flex-col gap-2">
        <h2 id="feedback-description" className="text-sm font-semibold text-fg">
          {t("console.feedback.description")}
        </h2>
        {/* Plain text, as written: React escapes it, and nothing renders Markdown here. */}
        <p className="whitespace-pre-wrap break-words rounded-lg border border-line bg-surface-raised p-3 text-sm text-fg">
          {data.description}
        </p>
      </section>
      {data.screenshot ? (
        <FeedbackScreenshot reportId={data.id} unmasked={data.screenshot.unmasked} />
      ) : null}
      <section aria-labelledby="feedback-context" className="flex flex-col gap-3">
        <h2 id="feedback-context" className="text-sm font-semibold text-fg">
          {t("console.feedback.context")}
        </h2>
        <dl className="flex flex-col gap-3 text-sm">
          {(["app", "route", "device"] as const).map((group) => (
            <div key={group} className="flex flex-col gap-0.5">
              <dt className="text-fg-secondary">{t(`feedback.groups.${group}`)}</dt>
              <dd className="text-fg">
                {context[group] !== undefined ? (
                  <GroupValues group={group} context={context} />
                ) : (
                  <span className="text-xs text-fg-muted">{t("console.feedback.notSent")}</span>
                )}
              </dd>
            </div>
          ))}
          {data.origin && data.origin !== "menu" ? (
            // The server-built agent context (role, tools, failures, outcome), if the reporter kept it.
            <div className="flex flex-col gap-0.5">
              <dt className="text-fg-secondary">{t("console.feedback.assistant")}</dt>
              <dd className="text-fg">
                {data.agentContext ? (
                  <p className="break-words font-mono text-xs">
                    {agentContextParts(data.agentContext).join(" · ")}
                  </p>
                ) : (
                  <span className="text-xs text-fg-muted">
                    {t("console.feedback.assistantNone")}
                  </span>
                )}
              </dd>
            </div>
          ) : null}
        </dl>
      </section>
      <section aria-labelledby="feedback-errors" className="flex flex-col gap-2">
        <h2 id="feedback-errors" className="text-sm font-semibold text-fg">
          {t("feedback.groups.recentErrors")}
        </h2>
        {context.recentErrors ? (
          <ErrorList errors={context.recentErrors} />
        ) : (
          <p className="text-xs text-fg-muted">{t("console.feedback.notSent")}</p>
        )}
        {data.fingerprints && data.fingerprints.length > 0 ? (
          <ReportFingerprints reportId={data.id} fingerprints={data.fingerprints} />
        ) : null}
      </section>
      {/* Unsorted only: a grouped report needs no suggestion (feedback-triage-assist D1). */}
      {grouping.data && !group ? <AssistantSuggestion reportId={data.id} /> : null}
      <ReportGrouping reportId={data.id} type={data.type} />
    </Page>
  );
}

/**
 * Previous and Next (feedback-inbox-followups D5): the neighbours in the list the report was
 * opened from, read from that list's own (unaudited) query, the inbox's for its filters or the
 * group's members. Stepping is a navigation, so the neighbour's opening is one audited read like
 * any other; nothing is fetched for a report before it is opened. `[` and `]` step too.
 */
function ReportSteps({ reportId, from }: { reportId: string; from: FeedbackReportOrigin }) {
  const { t } = useTranslation();
  const inGroup = "group" in from;
  // Kept fresh for a while: each step remounts this, and refetching every loaded page per step
  // would read the whole list again (the list itself is never audited, but it is not free).
  const inbox = useInboxReports(
    queryOf(filtersOf(new URLSearchParams("inbox" in from ? from.inbox : ""))),
    { enabled: !inGroup, staleTime: STEP_LIST_STALE_MS },
  );
  const group = useFeedbackGroup(inGroup ? from.group : "", inGroup);
  const ids = inGroup
    ? (group.data?.members.map((r) => r.id) ?? [])
    : (inbox.data?.pages.flatMap((p) => p.reports.map((r) => r.id)) ?? []);
  const at = ids.indexOf(reportId);
  // The neighbours last seen around this report: a triage action that takes it out of the list
  // (added to a group from Unsorted, ungrouped from its group) keeps "go to the next one".
  const [seen, setSeen] = useState<{ reportId: string; previous?: string; next?: string }>({
    reportId: "",
  });
  const around =
    at >= 0
      ? { reportId, previous: at > 0 ? ids[at - 1] : undefined, next: ids[at + 1] }
      : seen.reportId === reportId
        ? seen
        : { reportId };
  if (
    at >= 0 &&
    (seen.reportId !== reportId || seen.previous !== around.previous || seen.next !== around.next)
  ) {
    setSeen(around);
  }
  const { previous, next } = around;
  const pages = inbox.data?.pages.length ?? 0;
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError } = inbox;
  // The next page (the keyset cursor) is read when it holds the next report (the last loaded
  // row), or, a few pages at most, when the report is not loaded yet (a reload, a link that
  // carries `?inbox=`). Once each, never in a loop: a failed page waits for Next.
  const wantsPage =
    !inGroup &&
    hasNextPage &&
    ((at >= 0 && at === ids.length - 1) ||
      (at < 0 && seen.reportId !== reportId && pages > 0 && pages < STEP_SEARCH_PAGES));
  useEffect(() => {
    if (wantsPage && !isFetchingNextPage && !isFetchNextPageError) {
      void fetchNextPage();
    }
  }, [wantsPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);
  const retryNext = !next && wantsPage && isFetchNextPageError;
  const goNext = () => {
    if (next) {
      navigate(consoleFeedbackPath(next, from));
    } else if (retryNext) {
      void fetchNextPage();
    }
  };
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      // Never from a field, and never behind an open dialog (a half-written group stays).
      if (
        isEditable(event.target) ||
        document.querySelector("[role='dialog'], [role='alertdialog']")
      ) {
        return;
      }
      if (event.key === "[" && previous) {
        event.preventDefault();
        navigate(consoleFeedbackPath(previous, from));
      } else if (event.key === "]" && (next || retryNext)) {
        event.preventDefault();
        goNext();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });
  return (
    <nav aria-label={t("console.feedback.steps")} className="flex items-center gap-1">
      <Button
        variant="outline"
        size="sm"
        disabled={!previous}
        title={t("console.feedback.previousKey")}
        onClick={() => previous && navigate(consoleFeedbackPath(previous, from))}
      >
        <ChevronLeft aria-hidden="true" />
        {t("console.feedback.previous")}
      </Button>
      <Button
        variant="outline"
        size="sm"
        disabled={!next && !retryNext}
        title={t("console.feedback.nextKey")}
        onClick={goNext}
      >
        {t("console.feedback.next")}
        <ChevronRight aria-hidden="true" />
      </Button>
    </nav>
  );
}
